import { useMemo, useState, useCallback } from 'react'
import { ImportPlayersModal } from '@/components/modal/ImportPlayersModal'
import { useClubMembers, DISPLAY_TABS, MemberDisplayTab, SortColumn, SortDir } from '../hooks/useClubMembers'
import { useClubEntitlementOptions } from '../hooks/useClubEntitlements'
import { useMemberActions } from '../hooks/useMemberActions'
import { useDebounce } from '@/hooks/useDebounce'
import { useExportClubMembers } from '@/hooks/useExportClubMembers'
import { MemberRow } from './MemberRow'
import { MemberFilterPopover, MemberFilterState, EMPTY_FILTER, countActiveFilters, describeActiveFilters } from './MemberFilterPopover'
import { TabBar, Spinner, EmptyState, CMButton } from './ui'
import { ClubMembersSearchFilter } from '@/types'
import { useCanUseCoaching } from '@/hooks/useCanUseCoaching'
import { useClubTools } from '../hooks/useClubTools'

interface ClubMembersTabProps {
  clubId: number
  isDirector: boolean
}

const SORT_COLUMNS: { id: SortColumn; label: string; className?: string }[] = [
  { id: 'name', label: 'Member' },
  { id: 'singles', label: 'Singles', className: 'text-center' },
  { id: 'doubles', label: 'Doubles', className: 'text-center' },
  { id: 'joined', label: 'Joined', className: 'text-center hidden md:table-cell' },
]

const SortArrow = ({ dir }: { dir: 'asc' | 'desc' | null }) => {
  if (!dir) return <span className="text-gray-300 ml-0.5">↕</span>
  return <span className="text-primary-500 ml-0.5">{dir === 'asc' ? '↑' : '↓'}</span>
}

/** Convert MemberFilterState to backend ClubMembersSearchFilter */
function toBackendFilter(
  filter: MemberFilterState,
  isDirector: boolean,
  operationEntitlements: Record<string, string[]>,
): ClubMembersSearchFilter | undefined {
  const backendFilter: ClubMembersSearchFilter = {}
  let hasFilter = false

  // Rating ranges
  const singlesMin = filter.singlesMin ? parseFloat(filter.singlesMin) : undefined
  const singlesMax = filter.singlesMax ? parseFloat(filter.singlesMax) : undefined
  const doublesMin = filter.doublesMin ? parseFloat(filter.doublesMin) : undefined
  const doublesMax = filter.doublesMax ? parseFloat(filter.doublesMax) : undefined

  if ((singlesMin && !isNaN(singlesMin)) || (singlesMax && !isNaN(singlesMax)) ||
      (doublesMin && !isNaN(doublesMin)) || (doublesMax && !isNaN(doublesMax))) {
    backendFilter.rating = {}
    if ((singlesMin && !isNaN(singlesMin)) || (singlesMax && !isNaN(singlesMax))) {
      backendFilter.rating.singles = {
        minRating: singlesMin && !isNaN(singlesMin) ? singlesMin : 2.0,
        maxRating: singlesMax && !isNaN(singlesMax) ? singlesMax : 8.0,
      }
    }
    if ((doublesMin && !isNaN(doublesMin)) || (doublesMax && !isNaN(doublesMax))) {
      backendFilter.rating.doubles = {
        minRating: doublesMin && !isNaN(doublesMin) ? doublesMin : 2.0,
        maxRating: doublesMax && !isNaN(doublesMax) ? doublesMax : 8.0,
      }
    }
    hasFilter = true
  }

  // clubInvitationType is not a user filter here: useClubMembers sets it to split the two pending
  // tabs by join type, and the endpoint ignores a lone invite type, so the popover's web/CSV
  // choice has no shape it can be sent as.

  // Entitlement filtering — operation toggles pre-select all benefits, so ents is always specific
  if (isDirector) {
    const opEntries = Object.entries(operationEntitlements)
    if (opEntries.length > 0) {
      const keys: string[] = []
      opEntries.forEach(([op, ents]) => {
        ents.forEach((e) => keys.push(`${op}:${e}`))
      })
      if (keys.length > 0) {
        backendFilter.entitlementKeys = keys
        hasFilter = true
      }
    }
  }

  // Rating status (rated/unrated)
  if (filter.ratingStatus === 'rated') {
    backendFilter.ratingStatus = 'RATED'
    hasFilter = true
  } else if (filter.ratingStatus === 'unrated') {
    backendFilter.ratingStatus = 'UNRATED'
    hasFilter = true
  }

  // Join date range
  if (filter.joinedAfter) {
    backendFilter.joinedAfter = new Date(filter.joinedAfter).toISOString()
    hasFilter = true
  }
  if (filter.joinedBefore) {
    // Add a day to include the end date
    const endDate = new Date(filter.joinedBefore)
    endDate.setDate(endDate.getDate() + 1)
    backendFilter.joinedBefore = endDate.toISOString()
    hasFilter = true
  }

  return hasFilter ? backendFilter : undefined
}

export const ClubMembersTab = ({ clubId, isDirector }: ClubMembersTabProps) => {
  // Whether to advertise the per-row coaching shortcut: the viewer can use coaching AND the club
  // actually holds a coaching capability the viewer may perform (server-gated).
  const canCoach = useCanUseCoaching()
  const { tools: coachingTools } = useClubTools(clubId, canCoach)
  const coachingEnabled = canCoach && coachingTools.length > 0

  // Search + debounce
  const [searchFilter, setSearchFilter] = useState('')
  const debouncedSearch = useDebounce<string>(searchFilter, 300)

  // Filter state
  const [filter, setFilter] = useState<MemberFilterState>(EMPTY_FILTER)

  // Sort state (managed here, passed to hook)
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>(null)

  const toggleSort = useCallback((col: SortColumn) => {
    if (sortColumn !== col) {
      setSortColumn(col)
      setSortDir('asc')
    } else if (sortDir === 'asc') {
      setSortDir('desc')
    } else {
      setSortColumn(null)
      setSortDir(null)
    }
  }, [sortColumn, sortDir])

  // Entitlement options for filter dropdowns (does not depend on member data)
  const { entitlementOptions, operationOptions } = useClubEntitlementOptions(clubId, isDirector)

  // Build backend filter from UI filter state
  const backendFilter = useMemo(
    () => toBackendFilter(filter, isDirector, filter.operationEntitlements),
    [filter, isDirector]
  )

  // The hook — now does server-side search/filter/sort
  const {
    members,
    total,
    hasMore,
    isLoading,
    isFetching,
    tab,
    offset,
    pageSize,
    countsByTab,
    changeTab: hookChangeTab,
    nextPage,
    prevPage,
  } = useClubMembers(clubId, {
    query: debouncedSearch || undefined,
    sortColumn,
    sortDir,
    filter: backendFilter,
  })

  // CSV import state
  const [showCSVImport, setShowCSVImport] = useState(false)

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  // Local removal tracking — instantly hides removed members
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set())

  // Export CSV
  const { exportMembers: handleExportCSV, isExporting, downloadRef } = useExportClubMembers(clubId)

  // Visible members after local removal
  const visibleMembers = useMemo(
    () => removedIds.size > 0 ? members.filter((m) => !removedIds.has(m.userId)) : members,
    [members, removedIds]
  )

  // All member filters are now server-side; visibleMembers only excludes locally-removed IDs
  const filteredMembers = visibleMembers

  const filterCount = useMemo(() => countActiveFilters(filter), [filter])
  const filterChips = useMemo(() => describeActiveFilters(filter, entitlementOptions), [filter, entitlementOptions])

  // Select all visible (filtered) members
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(filteredMembers.map((m) => m.userId)))
    } else {
      setSelectedIds(new Set())
    }
  }, [filteredMembers])

  const handleToggleSelect = useCallback((userId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }, [])

  const allVisibleSelected = filteredMembers.length > 0 && filteredMembers.every((m) => selectedIds.has(m.userId))

  const exitEditMode = () => {
    setIsEditMode(false)
    setSelectedIds(new Set())
  }

  const { remove, approve, removalLabel, isApproving, isBusy } = useMemberActions({
    clubId,
    tab,
    // Drop the acted-on rows from the table at once; the refetch the hook then issues brings the
    // tab counts up to date.
    onSettled: (userIds) => {
      setRemovedIds((prev) => new Set([...prev, ...userIds]))
      exitEditMode()
    },
  })

  const handleTabChange = (id: string) => {
    hookChangeTab(id as MemberDisplayTab)
    exitEditMode()
    setRemovedIds(new Set())
  }

  const handleBulkRemove = () => remove(Array.from(selectedIds))

  const visibleTotal = total - removedIds.size
  const showingFrom = visibleTotal > 0 ? offset + 1 : 0
  const showingTo = Math.min(offset + pageSize, visibleTotal)

  const anyFilterActive = searchFilter || filterCount > 0

  return (
    <div className="space-y-3">
      {/* Tabs + Edit button */}
      <div className="flex items-center justify-between gap-2">
        <TabBar
          tabs={DISPLAY_TABS.map(({ id, label }) => ({
            id,
            label,
            count: countsByTab[id],
          }))}
          activeTab={tab}
          onTabChange={handleTabChange}
          size="sm"
        />
        <div className="flex-1" />
        <div className="flex items-center gap-2 flex-shrink-0">
          {isDirector && (
            <button
              onClick={handleExportCSV}
              disabled={isExporting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isExporting ? (
                <Spinner size="sm" className="!w-3.5 !h-3.5 !border-white !border-t-transparent" />
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              )}
              {isExporting ? 'Exporting...' : 'Export'}
            </button>
          )}
          {isDirector && (
            <button
              onClick={() => setShowCSVImport(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 transition-colors"
            >
              <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Import
            </button>
          )}
          {isDirector && !isEditMode && (
            <button
              onClick={() => setIsEditMode(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 hover:bg-red-50 transition-colors"
            >
              Manage
            </button>
          )}
        </div>
      </div>

      {/* Edit mode toolbar */}
      {isEditMode && (
        <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-gray-50 border border-gray-200 text-xs">
          <span className="text-gray-600">
            {selectedIds.size} selected
          </span>
          <div className="flex items-center gap-2">
            {tab === 'PENDING_JOIN_REQUEST' && (
              <CMButton
                variant="primary"
                size="sm"
                onClick={() => approve(Array.from(selectedIds))}
                disabled={selectedIds.size === 0 || isBusy}
                className="text-xs py-1"
              >
                {isApproving ? 'Approving...' : `Approve (${selectedIds.size})`}
              </CMButton>
            )}
            <CMButton
              variant="danger"
              size="sm"
              onClick={handleBulkRemove}
              disabled={selectedIds.size === 0 || isBusy}
              className="text-xs py-1"
            >
              {isBusy ? 'Processing...' : `${removalLabel} (${selectedIds.size})`}
            </CMButton>
            <CMButton variant="muted" size="sm" onClick={exitEditMode} className="text-xs py-1">
              Cancel
            </CMButton>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by name or DUPR ID..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="w-full pl-7 pr-3 py-1.5 border border-gray-200 text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
        </div>
        <MemberFilterPopover
          filter={filter}
          onChange={setFilter}
          tab={tab}
          isDirector={isDirector}
          operationOptions={operationOptions}
          entitlementOptions={entitlementOptions}
        />
      </div>

      {/* Active filter chips */}
      {filterChips.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {filterChips.map((chip) => (
            <span key={chip} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-primary-50 border border-primary-200 text-[10px] text-primary-700">
              {chip}
            </span>
          ))}
          <button
            onClick={() => setFilter(EMPTY_FILTER)}
            className="text-[10px] text-gray-400 hover:text-gray-600 px-1"
          >
            clear
          </button>
        </div>
      )}

      {/* Member Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              {isEditMode && (
                <th className="py-1.5 px-2 w-8">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-3.5 h-3.5 accent-primary-600"
                  />
                </th>
              )}
              {SORT_COLUMNS.map((col) => (
                <th
                  key={col.id}
                  onClick={() => toggleSort(col.id)}
                  className={`text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider py-1.5 px-3 cursor-pointer hover:text-gray-700 select-none ${col.className || ''}`}
                >
                  {col.label}
                  <SortArrow dir={sortColumn === col.id ? sortDir : null} />
                </th>
              ))}
              <th className="py-1.5 px-3 w-px">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredMembers.map((member) => (
              <MemberRow
                key={member.userId}
                member={member}
                entitlements={isDirector ? member.entitlements : undefined}
                isEditMode={isEditMode}
                isSelected={selectedIds.has(member.userId)}
                onToggleSelect={handleToggleSelect}
                coachingEnabled={coachingEnabled}
                onApprove={tab === 'PENDING_JOIN_REQUEST' && isDirector ? (userId) => approve([userId]) : undefined}
              />
            ))}
          </tbody>
        </table>

        {isLoading && (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        )}

        {!isLoading && filteredMembers.length === 0 && (
          <EmptyState
            icon={
              <svg className="w-10 h-10 text-gray-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
            message={anyFilterActive
              ? 'No members match your filters'
              : `No ${DISPLAY_TABS.find((t) => t.id === tab)?.label.toLowerCase() || ''} members`
            }
            className="py-8"
          />
        )}
      </div>

      {/* Pagination */}
      {visibleTotal > 0 && (
        <div className="flex items-center justify-between pt-1 border-t border-gray-100">
          <span className="text-xs text-gray-500">
            {showingFrom}-{showingTo} of {visibleTotal}
            {filteredMembers.length !== visibleMembers.length && ` (${filteredMembers.length} shown)`}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={prevPage}
              disabled={offset === 0}
              className="px-2 py-1 border border-gray-200 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Prev
            </button>
            <button
              onClick={nextPage}
              disabled={!hasMore}
              className="px-2 py-1 border border-gray-200 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
            {isFetching && !isLoading && <Spinner size="sm" />}
          </div>
        </div>
      )}

      <ImportPlayersModal open={showCSVImport} onClose={() => setShowCSVImport(false)} clubId={String(clubId)} />
      {/* Hidden anchor for in-page download (no new tab, no popup blocker) */}
      <a ref={downloadRef} className="hidden" />

      {/* Export processing modal */}
      {isExporting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl px-8 py-6 max-w-sm w-full mx-4 flex flex-col items-center gap-4">
            <Spinner />
            <div className="text-center">
              <p className="text-sm font-semibold text-gray-900">Your export is being generated</p>
              <p className="text-xs text-gray-500 mt-1">Do not refresh or navigate away from this page.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
